import { ApolloError } from "@apollo/client";
import {
  ConsultantAssignCallListPayload,
  ConsultantAssignTagsPayload,
  ConsultantDeletePayload,
  ConsultantEmailPayload,
  ConsultantOrderQualityDocumentsPayload,
  ConsultantReassignBAPayload,
  ConsultantSmsPayload
} from "@bonliva-crm/common/bulk-actions";
import {
  BulkActionStatus,
  BulkActionType,
  useBulkActionMutation
} from "@bonliva-crm/graphql";
import locale from "@bonliva-crm/locale";
import { useSnackbarsPipeline } from "@bonliva-crm/ui";
import { translateSmsTextError } from "@components/sms/sms-text-hint";

type Props = {
  onCompleted?: () => void;
  onError?: (error: ApolloError) => void;
};

type ActionPayloadMapping =
  | { type: BulkActionType.ConsultantEmail; payload: ConsultantEmailPayload }
  | { type: BulkActionType.ConsultantReassignBA; payload: ConsultantReassignBAPayload }
  | { type: BulkActionType.ConsultantSMS; payload: ConsultantSmsPayload }
  | {
      type: BulkActionType.ConsultantAssignCallList;
      payload: ConsultantAssignCallListPayload;
    }
  | { type: BulkActionType.ConsultantDelete; payload: ConsultantDeletePayload }
  | { type: BulkActionType.ConsultantAssignTags; payload: ConsultantAssignTagsPayload }
  | {
      type: BulkActionType.ConsultantOrderQualityDocuments;
      payload: ConsultantOrderQualityDocumentsPayload;
    };

export const useBulkActions = (props: Props) => {
  const snack = useSnackbarsPipeline();

  const [bulkActionMutation] = useBulkActionMutation({
    onCompleted: (data) => {
      // A refused payload comes back as status Error, not as a GraphQL error.
      if (data.bulkAction.status === BulkActionStatus.Error) {
        snack.showError(data.bulkAction.error, locale.ErrorOccurred);
        return;
      }

      snack.showSuccess(locale.youWillBeNotifiedViaEmail, locale.massActionQueuedTitle);
      props.onCompleted && props.onCompleted();
    },
    onError: (error) => {
      console.error(error);
      snack.showError(
        translateSmsTextError(error) ?? error.message,
        locale.ErrorOccurred
      );
      props.onError && props.onError(error);
    }
  });

  const bulkAction = (input: ActionPayloadMapping) => {
    bulkActionMutation({
      variables: {
        input: input
      }
    });
  };

  const sendConsultantEmail = (data: ConsultantEmailPayload) => {
    bulkAction({
      type: BulkActionType.ConsultantEmail,
      payload: data
    });
  };

  const sendConsultantSMS = (data: ConsultantSmsPayload) => {
    bulkAction({
      type: BulkActionType.ConsultantSMS,
      payload: data
    });
  };

  const reassignConsultantBa = (data: ConsultantReassignBAPayload) => {
    bulkAction({
      type: BulkActionType.ConsultantReassignBA,
      payload: data
    });
  };

  const assignConsultantTags = (data: ConsultantAssignTagsPayload) => {
    bulkAction({
      type: BulkActionType.ConsultantAssignTags,
      payload: data
    });
  };

  const assignConsultantCallList = (data: ConsultantAssignCallListPayload) => {
    bulkAction({
      type: BulkActionType.ConsultantAssignCallList,
      payload: data
    });
  };

  const deleteConsultants = (data: ConsultantDeletePayload) => {
    bulkAction({
      type: BulkActionType.ConsultantDelete,
      payload: data
    });
  };

  const orderQualityDocuments = (data: ConsultantOrderQualityDocumentsPayload) => {
    bulkAction({
      type: BulkActionType.ConsultantOrderQualityDocuments,
      payload: data
    });
  };

  return {
    sendConsultantEmail,
    sendConsultantSMS,
    reassignConsultantBa,
    assignConsultantCallList,
    deleteConsultants,
    assignConsultantTags,
    orderQualityDocuments
  };
};
